// client/src/v2/revenuecat.ts
//
// Camada fina sobre o SDK do RevenueCat. Centraliza:
//   - configure() — chamado 1x no startup com a Public SDK Key da plataforma
//   - logIn(userId) — chamado quando user autentica
//   - logOut() — chamado no logout
//   - getOfferings() — busca packages pra mostrar no paywall
//   - purchasePackage(pkg) — dispara fluxo nativo de compra (Apple ou Google)
//
// Roda em iOS E Android nativos (Capacitor). Em web fica no-op.
// A chave de API é escolhida por plataforma via rcApiKey():
//   iOS     → RC_IOS_API_KEY (appl_...)
//   Android → RC_ANDROID_API_KEY (goog_...)

import { isNativeApp, getPlatform } from './platform';
import { tlog } from './telemetry';

const RC_IOS_API_KEY = 'appl_nPgHjfRskuawwZHZmNdoTMguLlS';
const RC_ANDROID_API_KEY = 'goog_EVEAtGdxOhEjMqGQdcrAXuFnAGM';

// Escolhe a Public SDK Key certa pra plataforma atual. Null em web (no-op).
function rcApiKey(): string | null {
  const p = getPlatform();
  if (p === 'ios') return RC_IOS_API_KEY;
  if (p === 'android') return RC_ANDROID_API_KEY;
  return null;
}

// Remote logging — manda evento pro backend /api/debug-log pra
// poder debugar de fora (sem precisar Mac + Safari Web Inspector).
function rclog(tag: string, data?: any): void {
  try {
    const payload = { tag: 'rc:' + tag, data: data === undefined ? null : data };
    if (typeof console !== 'undefined') {
      try { console.log('[rc]', tag, data); } catch {}
    }
    // Novo: vai pro batch da telemetria (correlacionado com session ID)
    try { tlog('rc:' + tag, data); } catch {}
    // Legado: endpoint antigo /api/debug-log (mantido pra compat)
    fetch('/api/debug-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

let PurchasesRef: any = null;
let configured = false;
// A configuração em andamento, para quem chega no meio dela esperar em vez de
// desistir (o login logo depois do boot caía em "not_ready").
let configurando: Promise<void> | null = null;

// O SDK VEM EMBRULHADO EM UM OBJETO — NUNCA DEVOLVA O PLUGIN SOLTO DE UMA
// FUNÇÃO async.
//
// `Purchases` é o proxy do Capacitor: QUALQUER propriedade lida nele vira uma
// chamada ao nativo — inclusive `then`. Uma função async que devolve um objeto
// com `then` faz o JavaScript tratá-lo como promessa e chamar
// `Purchases.then(resolve, reject)`. O nativo responde "Purchases.then() is not
// implemented on android" e NUNCA chama resolve nem reject: o `await
// ensureSDK()` ficava pendurado para sempre (log da produção, 03/10: usuária
// Android, três vezes `rc:ensureSDK:start` seguido de `error:promise` e mais
// nada). Por isso "Restaurar compras" não respondia, a troca de conta não
// trocava a identidade de compra e "Sair da conta" no app esperava sem fim.
// Embrulhado em `{ Purchases }`, o objeto devolvido não tem `then`.
async function ensureSDK(): Promise<{ Purchases: any } | null> {
  rclog('ensureSDK:start', { isNative: isNativeApp(), platform: getPlatform(), hasRef: !!PurchasesRef });
  if (!isNativeApp()) {
    rclog('ensureSDK:not_native', null);
    return null;
  }
  if (PurchasesRef) return { Purchases: PurchasesRef };
  try {
    rclog('ensureSDK:importing', null);
    const mod = await import('@revenuecat/purchases-capacitor');
    PurchasesRef = mod.Purchases;
    rclog('ensureSDK:imported', { hasPurchases: !!PurchasesRef });
    return PurchasesRef ? { Purchases: PurchasesRef } : null;
  } catch (e: any) {
    rclog('ensureSDK:import_failed', { msg: e?.message, stack: String(e?.stack || '').slice(0, 500) });
    return null;
  }
}

// Corrida com prazo: uma chamada nativa que não volta não pode prender a tela.
function comPrazo<T>(p: Promise<T>, ms: number, oque: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(oque + ' timeout ' + Math.round(ms / 1000) + 's')), ms)),
  ]);
}

export async function configureRevenueCat(appUserID?: string, appUserEmail?: string): Promise<void> {
  rclog('configure:enter', { alreadyConfigured: configured });
  if (configured) return;

  // Anti re-entrant: se ja tem um configure rodando, espera
  if ((globalThis as any).__rcConfiguring) {
    rclog('configure:already_running', null);
    return;
  }
  (globalThis as any).__rcConfiguring = true;
  let terminou: () => void = () => {};
  configurando = new Promise<void>((r) => { terminou = r; });

  try {
    rclog('configure:step1_check_native', { isNative: isNativeApp(), platform: getPlatform() });
    if (!isNativeApp()) {
      rclog('configure:not_native', null);
      return;
    }

    rclog('configure:step2_check_capacitor', null);
    const cap: any = (window as any).Capacitor;
    if (!cap?.Plugins?.Purchases) {
      rclog('configure:no_plugin', { hasCap: !!cap, hasPlugins: !!cap?.Plugins, hasPurchases: !!cap?.Plugins?.Purchases });
      return;
    }
    rclog('configure:step3_plugin_found', { keys: Object.keys(cap.Plugins).slice(0, 20) });

    rclog('configure:step4_importing', null);
    let Purchases: any;
    try {
      const mod = await import('@revenuecat/purchases-capacitor');
      Purchases = mod.Purchases;
      PurchasesRef = Purchases;
      rclog('configure:step5_imported', { hasPurchases: !!Purchases });
    } catch (impErr: any) {
      rclog('configure:import_error', { msg: impErr?.message });
      return;
    }

    const apiKey = rcApiKey();
    if (!apiKey) {
      rclog('configure:no_key', { platform: getPlatform() });
      return;
    }

    rclog('configure:step6_calling', { apiKey: apiKey.slice(0, 12) + '...', platform: getPlatform() });
    try {
      const cfg: any = { apiKey };
      if (appUserID) cfg.appUserID = appUserID;
      rclog('configure:step6_with_user', { hasUserId: !!appUserID });
      const configurePromise = Purchases.configure(cfg);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('configure timeout 10s')), 10000)
      );
      const result = await Promise.race([configurePromise, timeoutPromise]);
      configured = true;
      rclog('configure:step7_ok', { result: typeof result });
      // Identifica o cliente por email no dashboard do RevenueCat (facilita suporte;
      // antes so aparecia o UUID). $email e atributo reservado do RC.
      try {
        if (appUserEmail && Purchases.setAttributes) {
          await Purchases.setAttributes({ '$email': appUserEmail });
          rclog('configure:email_set', null);
        }
      } catch (emErr: any) { rclog('configure:email_set_fail', { msg: emErr?.message }); }
    } catch (e: any) {
      rclog('configure:step7_failed', {
        msg: e?.message,
        name: e?.name,
        code: e?.code,
        stack: String(e?.stack || '').slice(0, 200),
      });
    }
  } finally {
    (globalThis as any).__rcConfiguring = false;
    configurando = null;
    terminou();
  }
}

// O logIn em andamento. A compra espera por ele (MOB-18): se a conta tem um
// alias anônimo e a compra sai ANTES de o logIn terminar, o RevenueCat não a
// transfere para a conta — e o webhook ignora compra anônima.
let loginEmAndamento: Promise<void> | null = null;

/**
 * Espera o logIn em andamento terminar, com prazo. NUNCA trava a compra: se o
 * logIn falhar ou demorar mais que o prazo, volta do mesmo jeito. Devolve
 * true quando teve de esperar (para a tela mostrar "Preparando…").
 */
export async function esperarIdentidadeRC(prazoMs = 5000): Promise<boolean> {
  const p = loginEmAndamento;
  if (!p) return false;
  rclog('compra:espera_login', { prazoMs });
  try { await comPrazo(p, prazoMs, 'compra:espera_login'); rclog('compra:login_pronto', null); }
  catch (e: any) { rclog('compra:segue_sem_login', { msg: e?.message }); }
  return true;
}

/** Há um logIn em andamento agora? (a tela decide se mostra "Preparando…") */
export function identidadeRCPendente(): boolean { return !!loginEmAndamento; }

export function loginRevenueCat(userId: string): Promise<void> {
  if (!userId) return Promise.resolve();
  const p = fazerLogin(userId);
  loginEmAndamento = p;
  // Só limpa se ainda for este (um login mais novo pode ter começado).
  const limpar = () => { if (loginEmAndamento === p) loginEmAndamento = null; };
  p.then(limpar, limpar);
  return p;
}

async function fazerLogin(userId: string): Promise<void> {
  rclog('login:start', { userId: userId.slice(0, 8) + '...' });
  // Chegou no meio do configure (boot): espera ele terminar, com prazo.
  if (!configured && configurando) {
    try { await comPrazo(configurando, 12000, 'login:wait_configure'); } catch (_) { /* segue e decide abaixo */ }
  }
  const sdk = await ensureSDK();
  const Purchases = sdk?.Purchases;
  if (!Purchases || !configured) {
    rclog('login:not_ready', { hasPurchases: !!Purchases, configured });
    return;
  }
  try {
    const r: any = await comPrazo(Purchases.logIn({ appUserID: userId }), 15000, 'logIn');
    rclog('login:ok', { created: !!r?.created });
  } catch (e: any) {
    rclog('login:failed', { msg: e?.message });
  }
}

export async function logoutRevenueCat(): Promise<void> {
  const sdk = await ensureSDK();
  const Purchases = sdk?.Purchases;
  if (!Purchases || !configured) return;
  // Sair da conta não espera a loja mais que alguns segundos (best-effort). O
  // RC recusa logOut de usuário anônimo — é o esperado, e cai no catch.
  try {
    await comPrazo(Purchases.logOut(), 5000, 'logOut');
    rclog('logout:ok', null);
  } catch (e: any) {
    rclog('logout:failed', { msg: e?.message });
  }
}

export interface RCPackage {
  identifier: string;
  product: {
    identifier: string;
    priceString: string;
    price: number;
    currencyCode: string;
    title: string;
    description: string;
  };
  /** Objeto ORIGINAL do RC SDK — NUNCA remapear, passar inteiro pro purchasePackage */
  _raw: any;
}

export async function getOfferingPackages(): Promise<RCPackage[]> {
  rclog('getOff:enter', { configured });
  if (!isNativeApp()) return [];

  if (!configured) {
    rclog('getOff:not_configured_trying', null);
    await configureRevenueCat();
    if (!configured) {
      rclog('getOff:still_not_configured_giveup', null);
      return [];
    }
  }

  const cap: any = (window as any).Capacitor;
  if (!cap?.Plugins?.Purchases) {
    rclog('getOff:no_plugin', null);
    return [];
  }
  const Purchases = cap.Plugins.Purchases;

  rclog('getOff:calling_native', null);
  try {
    const offeringsPromise = Purchases.getOfferings();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('getOfferings timeout 10s')), 10000)
    );
    const offerings: any = await Promise.race([offeringsPromise, timeoutPromise]);
    const cur = offerings?.current;
    const all = offerings?.all || {};
    rclog('getOff:received', {
      hasCurrent: !!cur,
      currentId: cur?.identifier,
      packagesCount: Array.isArray(cur?.availablePackages) ? cur.availablePackages.length : 0,
      allKeys: Object.keys(all),
    });
    if (!cur || !Array.isArray(cur.availablePackages)) return [];
    const mapped = cur.availablePackages.map((p: any) => ({
      identifier: p.identifier,
      product: {
        identifier: p.product?.identifier,
        priceString: p.product?.priceString,
        price: p.product?.price,
        currencyCode: p.product?.currencyCode,
        title: p.product?.title,
        description: p.product?.description,
      },
      _raw: p,  // mantém o objeto raw pra purchasePackage (precisa presentedOfferingContext)
    }));
    rclog('getOff:mapped', { ids: mapped.map((p: any) => p.identifier) });
    return mapped;
  } catch (e: any) {
    rclog('getOff:exception', {
      msg: e?.message,
      name: e?.name,
      code: e?.code,
      stack: String(e?.stack || '').slice(0, 300),
    });
    return [];
  }
}

export async function purchaseRCPackage(rcPackage: any): Promise<{
  ok: boolean;
  cancelled?: boolean;
  error?: string;
  activeEntitlement?: string | null;
}> {
  rclog('purchase:enter', { pkg: rcPackage?.identifier, productId: rcPackage?.product?.identifier });
  if (!isNativeApp()) return { ok: false, error: 'not_native' };

  const cap: any = (window as any).Capacitor;
  if (!cap?.Plugins?.Purchases) {
    rclog('purchase:no_plugin', null);
    return { ok: false, error: 'no_plugin' };
  }
  const Purchases = cap.Plugins.Purchases;

  rclog('purchase:calling_native', { pkg: rcPackage?.identifier });
  try {
    // SEM timeout: o fluxo de pagamento nativo pode legitimamente levar minutos
    // (usuario adicionando/corrigindo cartao). O race de 30s matava o sucesso da
    // compra e deixava o plano sem liberar mesmo com o Google ja tendo cobrado.
    // A auto-cura no backend (/api/rc/sync-plan) cobre qualquer caso residual.
    const result: any = await Purchases.purchasePackage({ aPackage: rcPackage });
    const entitlements = result?.customerInfo?.entitlements?.active || {};
    const activeKeys = Object.keys(entitlements);
    rclog('purchase:ok', { active: activeKeys, trans: !!result?.transaction });
    return { ok: true, activeEntitlement: activeKeys[0] || null };
  } catch (e: any) {
    const cancelled = !!(e?.userCancelled || e?.code === 'PURCHASE_CANCELLED' || /cancel/i.test(e?.message || ''));
    rclog('purchase:failed', {
      msg: e?.message,
      name: e?.name,
      code: e?.code,
      cancelled,
      underlying: e?.underlyingErrorMessage,
      stack: String(e?.stack || '').slice(0, 300),
    });
    return { ok: false, cancelled, error: e?.message || 'unknown' };
  }
}

export async function restorePurchasesRC(): Promise<{
  ok: boolean;
  activeEntitlement?: string | null;
}> {
  rclog('restore:enter', { configured });
  const sdk = await ensureSDK();
  const Purchases = sdk?.Purchases;
  if (!Purchases || !configured) {
    rclog('restore:not_ready', { hasPurchases: !!Purchases, configured });
    return { ok: false };
  }
  try {
    // Restaurar consulta a loja: pode levar alguns segundos, mas não pode
    // deixar o botão sem resposta para sempre.
    const result: any = await comPrazo(Purchases.restorePurchases(), 30000, 'restorePurchases');
    const entitlements = result?.customerInfo?.entitlements?.active || {};
    const activeKeys = Object.keys(entitlements);
    rclog('restore:ok', { active: activeKeys });
    return { ok: true, activeEntitlement: activeKeys[0] || null };
  } catch (e: any) {
    rclog('restore:failed', { msg: e?.message, code: e?.code });
    return { ok: false };
  }
}

export async function getActiveEntitlement(): Promise<string | null> {
  const sdk = await ensureSDK();
  const Purchases = sdk?.Purchases;
  if (!Purchases || !configured) return null;
  try {
    const info: any = await comPrazo(Purchases.getCustomerInfo(), 15000, 'getCustomerInfo');
    const active = info?.customerInfo?.entitlements?.active || {};
    return Object.keys(active)[0] || null;
  } catch { return null; }
}

/**
 * Assinar um Mentor (Círculo do Mentor, 14/10/2026). O app é grátis; o que se
 * paga é o Mentor, e cada Mentor vende por UM dos 3 SKUs que já existem nas
 * lojas (mentors.price_sku). A compra é a mesma de sempre — o package
 * `{sku}_monthly|yearly` da offering atual —; o servidor transforma a compra em
 * mentor_subscriptions (webhook + /api/rc/sync-plan).
 * `pacote` vem pronto de GET /api/mentores/:slug (compra.pacotes).
 */
export async function assinarMentor(pacote: string): Promise<{
  ok: boolean;
  cancelled?: boolean;
  error?: string;
}> {
  if (!isNativeApp()) return { ok: false, error: 'not_native' };
  await esperarIdentidadeRC(5000);
  const pkgs = await getOfferingPackages();
  const alvo = pkgs.find(p => p.identifier === pacote);
  if (!alvo) {
    rclog('mentor:sem_pacote', { pacote, ids: pkgs.map(p => p.identifier) });
    return { ok: false, error: 'produto_indisponivel' };
  }
  const r = await purchaseRCPackage(alvo._raw || alvo);
  return { ok: r.ok, cancelled: r.cancelled, error: r.error };
}
